import type { SessionListItem, SessionMessage, SessionMetadata } from '../../store/api';

export type SessionSource = 'playground' | 'api' | 'unknown';
export type SessionStatus = 'success' | 'failed' | 'unknown';

export function deriveSource(meta?: SessionMetadata | null): SessionSource {
  const raw = (meta?.source || '').toLowerCase();
  if (raw === 'playground' || raw === 'clone' || raw === 'cloned') return 'playground';
  if (raw === 'api' || raw === 'direct' || raw === 'direct_api') return 'api';
  if (meta?.applicationId) return 'api';
  return 'unknown';
}

export function deriveStatus(meta?: SessionMetadata | null): SessionStatus {
  const raw = (meta?.status || '').toLowerCase();
  if (raw === 'failed' || raw === 'error' || raw === 'failure') return 'failed';
  if (raw === 'success' || raw === 'succeeded' || raw === 'completed' || raw === 'ok') {
    return 'success';
  }
  return 'unknown';
}

export function formatSessionId(id: string, keep = 8): string {
  if (!id) return '—';
  if (id.length <= keep + 2) return id;
  return `#${id.slice(0, keep)}`;
}

export function relativeTime(iso: string | undefined | null): string {
  if (!iso) return '—';
  const parsed = !/Z|[+-]\d{2}:\d{2}$/.test(iso) ? `${iso}Z` : iso;
  const ts = new Date(parsed).getTime();
  if (Number.isNaN(ts)) return '—';
  const diff = Date.now() - ts;
  if (diff < 0) return 'just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function approxDuration(
  createdAt?: string | null,
  updatedAt?: string | null,
): string {
  if (!createdAt || !updatedAt) return '—';
  const start = new Date(
    !/Z|[+-]\d{2}:\d{2}$/.test(createdAt) ? `${createdAt}Z` : createdAt,
  ).getTime();
  const end = new Date(
    !/Z|[+-]\d{2}:\d{2}$/.test(updatedAt) ? `${updatedAt}Z` : updatedAt,
  ).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return '—';
  const ms = end - start;
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const mins = Math.floor(ms / 60_000);
  const secs = Math.round((ms % 60_000) / 1000);
  return `${mins}m ${secs}s`;
}

function truncateTopic(text: string, max = 80): string {
  const cleaned = text.replace(/\s+/g, ' ').trim();
  if (!cleaned) return '';
  return cleaned.length > max ? `${cleaned.slice(0, max - 3)}…` : cleaned;
}

/**
 * Session list / preview heading.
 *
 * Preference: conversation messages (selected session) → harness ``last_query``
 * (durable topic for every list row) → short temporary placeholder.
 * Full AI one-liners remain a later product enabler.
 */
export function provisionalSummary(
  session: SessionListItem,
  messages?: SessionMessage[] | null,
): string {
  const status = deriveStatus(session.metadata);
  if (status === 'failed') {
    const fromQuery = truncateTopic(session.metadata?.lastQuery || '');
    if (fromQuery) return fromQuery;
    return 'Failure summary unavailable — status trail not enriched yet';
  }
  if (messages && messages.length > 0) {
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUser?.content) {
      const fromMessage = truncateTopic(lastUser.content);
      if (fromMessage) return fromMessage;
    }
    return `${messages.length} message${messages.length === 1 ? '' : 's'}`;
  }
  const fromQuery = truncateTopic(session.metadata?.lastQuery || '');
  if (fromQuery) return fromQuery;
  return `Untitled session · ${formatSessionId(session.sessionId)}`;
}

export function sourceLabel(source: SessionSource): string {
  if (source === 'playground') return 'Playground';
  if (source === 'api') return 'API';
  return 'Unknown';
}

export function statusLabel(status: SessionStatus): string {
  if (status === 'failed') return 'Failed';
  if (status === 'success') return 'Success';
  return 'Unknown';
}

export function withinTimeRange(
  iso: string | undefined | null,
  range: '24h' | '7d' | '30d' | 'all',
): boolean {
  if (range === 'all' || !iso) return true;
  const parsed = !/Z|[+-]\d{2}:\d{2}$/.test(iso) ? `${iso}Z` : iso;
  const ts = new Date(parsed).getTime();
  if (Number.isNaN(ts)) return true;
  const now = Date.now();
  const windows: Record<'24h' | '7d' | '30d', number> = {
    '24h': 24 * 60 * 60 * 1000,
    '7d': 7 * 24 * 60 * 60 * 1000,
    '30d': 30 * 24 * 60 * 60 * 1000,
  };
  return now - ts <= windows[range];
}

/** Pair user→assistant messages into exchange groups for dividers. */
export function groupExchanges(messages: SessionMessage[]): SessionMessage[][] {
  const groups: SessionMessage[][] = [];
  let current: SessionMessage[] = [];
  for (const msg of messages) {
    if (msg.role === 'user' && current.length > 0) {
      groups.push(current);
      current = [msg];
    } else {
      current.push(msg);
    }
  }
  if (current.length) groups.push(current);
  return groups;
}
